<script setup>
import { ref, computed, onMounted, watch, nextTick } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import Modal from 'dashboard/components/Modal.vue';
import axios from 'axios';
import Auth from 'dashboard/api/auth';

const props = defineProps({
  show: {
    type: Boolean,
    default: false,
  },
  plan: {
    type: Object,
    required: true,
  },
  accountId: {
    type: Number,
    required: true,
  },
});

const emit = defineEmits(['close', 'success']);

const { t } = useI18n();

const billingType = ref('monthly');
const months = ref(1);
const processing = ref(false);
const paypalLoaded = ref(false);
const paypalButtons = ref(null);

const getDiscountPercentage = () => {
  if (billingType.value === 'yearly') return 0;

  switch (months.value) {
    case 3:
      return 10;
    case 6:
      return 20;
    case 12:
      return 34;
    default:
      return 0;
  }
};

const totalAmount = computed(() => {
  if (billingType.value === 'monthly') {
    const baseAmount = props.plan.price_month * months.value;
    const discountPercentage = getDiscountPercentage();
    const discountAmount = (baseAmount * discountPercentage) / 100;
    return (baseAmount - discountAmount).toFixed(2);
  }
  return props.plan.price_year.toFixed(2);
});

const originalAmount = computed(() => {
  if (billingType.value === 'monthly') {
    return (props.plan.price_month * months.value).toFixed(2);
  }
  return props.plan.price_year.toFixed(2);
});

const discountPercentage = computed(() => {
  return getDiscountPercentage();
});

const updateSubscription = async (orderId, captureId) => {
  try {
    const authData = Auth.getAuthData();
    const { apiHost = '' } = window.chatwootConfig || {};

    const response = await axios.post(
      `${apiHost}/api/v1/accounts/${props.accountId}/subscription_updates`,
      {
        plan_id: props.plan.id,
        billing_type: billingType.value,
        months: billingType.value === 'monthly' ? months.value : null,
        paypal_order_id: orderId,
        paypal_capture_id: captureId,
      },
      {
        headers: {
          'access-token': authData['access-token'],
          'token-type': authData['token-type'],
          client: authData.client,
          expiry: authData.expiry,
          uid: authData.uid,
        },
      }
    );

    if (response.data.success) {
      useAlert(t('SUBSCRIPTION_SETTINGS.UPGRADE.SUCCESS'));
      emit('success', response.data);
      emit('close');
    } else {
      useAlert(response.data.error || t('SUBSCRIPTION_SETTINGS.UPGRADE.ERROR'));
    }
  } catch (error) {
    useAlert(
      error.response?.data?.error || t('SUBSCRIPTION_SETTINGS.UPGRADE.ERROR')
    );
  } finally {
    processing.value = false;
  }
};

const renderPayPalButtons = () => {
  if (!paypalButtons.value || !window.paypal) return;

  paypalButtons.value.innerHTML = '';

  window.paypal
    .Buttons({
      createOrder: (data, actions) => {
        return actions.order.create({
          purchase_units: [
            {
              description: `${props.plan.name} - ${billingType.value === 'monthly' ? `${months.value} ${t('SUBSCRIPTION_SETTINGS.UPGRADE.MONTHS')}` : t('SUBSCRIPTION_SETTINGS.UPGRADE.YEARLY')}`,
              amount: {
                currency_code: 'USD',
                value: totalAmount.value,
              },
            },
          ],
        });
      },
      onApprove: async (data, actions) => {
        processing.value = true;
        try {
          const details = await actions.order.capture();
          await updateSubscription(data.orderID, details.id);
        } catch (error) {
          useAlert(t('SUBSCRIPTION_SETTINGS.UPGRADE.PAYMENT_ERROR'));
          processing.value = false;
        }
      },
      onError: () => {
        useAlert(t('SUBSCRIPTION_SETTINGS.UPGRADE.PAYMENT_ERROR'));
        processing.value = false;
      },
    })
    .render(paypalButtons.value);
};

const loadPayPalScript = () => {
  if (window.paypal) {
    paypalLoaded.value = true;
    nextTick(() => renderPayPalButtons());
    return;
  }

  const script = document.createElement('script');
  script.src =
    // 'https://www.paypal.com/sdk/js?client-id=AfTHUv128_72p7IyLAwC7E7wFd1uGgD7j9Nnh66HwIblkavg3YjthXQYjLsPDj-gTyEED1-RRhGFtLtW&currency=USD';

    'https://www.paypal.com/sdk/js?client-id=AR_PjhJqq3zKZwIor39_2p_S_K0cWpe435UfpVivc5CaNjarBIpZasDFNqnYWooXlBuQL5envoU6NSpK&currency=USD';

  script.addEventListener('load', () => {
    paypalLoaded.value = true;
    nextTick(() => renderPayPalButtons());
  });
  document.body.appendChild(script);
};

const handleBillingTypeChange = () => {
  if (paypalLoaded.value) {
    renderPayPalButtons();
  }
};

const handleMonthsChange = () => {
  if (paypalLoaded.value && billingType.value === 'monthly') {
    renderPayPalButtons();
  }
};

watch(
  () => props.show,
  newVal => {
    if (newVal) {
      nextTick(() => loadPayPalScript());
    }
  }
);

onMounted(() => {
  if (props.show) {
    nextTick(() => loadPayPalScript());
  }
});
</script>

<template>
  <Modal :show="show" @close="emit('close')">
    <div class="w-full max-w-2xl">
      <div
        class="flex items-center justify-between p-6 border-b border-n-slate-6"
      >
        <h2 class="text-2xl font-semibold text-n-slate-12">
          {{ $t('SUBSCRIPTION_SETTINGS.UPGRADE.TITLE') }}
        </h2>
        <button
          class="text-n-slate-11 hover:text-n-slate-12"
          @click="emit('close')"
        >
          <fluent-icon icon="dismiss" size="24" />
        </button>
      </div>

      <div class="p-6 max-h-[70vh] overflow-y-auto">
        <div class="mb-6 p-4 bg-n-slate-2 rounded-lg border border-n-slate-6">
          <h3 class="text-lg font-semibold text-n-slate-12 mb-1">
            {{ plan.name }}
          </h3>
          <p class="text-sm text-n-slate-11">
            {{ $t('SUBSCRIPTION_SETTINGS.UPGRADE.SELECTED_PLAN') }}
          </p>
        </div>

        <div class="mb-6">
          <label class="block text-sm font-medium text-n-slate-12 mb-3">
            {{ $t('SUBSCRIPTION_SETTINGS.UPGRADE.BILLING_TYPE') }}
          </label>
          <div class="grid grid-cols-2 gap-4">
            <button
              class="p-4 border rounded-lg transition-all text-left"
              :class="
                billingType === 'monthly'
                  ? 'border-n-brand bg-n-brand/5 text-n-brand'
                  : 'border-n-slate-6 text-n-slate-11 hover:border-n-slate-7'
              "
              @click="
                billingType = 'monthly';
                handleBillingTypeChange();
              "
            >
              <div class="text-sm font-medium">
                {{ $t('SUBSCRIPTION_SETTINGS.UPGRADE.MONTHLY') }}
              </div>
              <div class="text-xs mt-1 opacity-80">
                {{ `$${plan.price_month}` }}
                {{ $t('SUBSCRIPTION_SETTINGS.UPGRADE.PER_MONTH') }}
              </div>
            </button>
            <button
              class="p-4 border rounded-lg transition-all text-left"
              :class="
                billingType === 'yearly'
                  ? 'border-n-brand bg-n-brand/5 text-n-brand'
                  : 'border-n-slate-6 text-n-slate-11 hover:border-n-slate-7'
              "
              @click="
                billingType = 'yearly';
                handleBillingTypeChange();
              "
            >
              <div class="text-sm font-medium">
                {{ $t('SUBSCRIPTION_SETTINGS.UPGRADE.YEARLY') }}
              </div>
              <div class="text-xs mt-1 opacity-80">
                {{ `$${plan.price_year}` }}
                {{ $t('SUBSCRIPTION_SETTINGS.UPGRADE.PER_YEAR') }}
              </div>
            </button>
          </div>
        </div>

        <div v-if="billingType === 'monthly'" class="mb-6">
          <label class="block text-sm font-medium text-n-slate-12 mb-3">
            {{ $t('SUBSCRIPTION_SETTINGS.UPGRADE.DURATION') }}
          </label>
          <select
            v-model.number="months"
            class="w-full px-4 py-3 border border-n-slate-6 rounded-lg bg-n-slate-1 text-n-slate-12 focus:outline-none focus:ring-2 focus:ring-n-brand"
            @change="handleMonthsChange"
          >
            <option :value="1">
              {{ $t('SUBSCRIPTION_SETTINGS.UPGRADE.DURATION_1_MONTH') }}
            </option>
            <option :value="3">
              {{
                $t('SUBSCRIPTION_SETTINGS.UPGRADE.DURATION_3_MONTHS_DISCOUNT')
              }}
            </option>
            <option :value="6">
              {{
                $t('SUBSCRIPTION_SETTINGS.UPGRADE.DURATION_6_MONTHS_DISCOUNT')
              }}
            </option>
            <option :value="12">
              {{
                $t('SUBSCRIPTION_SETTINGS.UPGRADE.DURATION_12_MONTHS_DISCOUNT')
              }}
            </option>
          </select>
        </div>

        <div class="mb-6 p-5 bg-n-brand/5 rounded-lg border border-n-brand/20">
          <div
            v-if="discountPercentage > 0"
            class="mb-3 pb-3 border-b border-n-brand/20"
          >
            <div class="flex items-center justify-between mb-1">
              <span class="text-sm text-n-slate-11">
                {{ $t('SUBSCRIPTION_SETTINGS.UPGRADE.SUBTOTAL') }}
              </span>
              <span class="text-sm text-n-slate-11 line-through">
                {{ `$${originalAmount}` }}
              </span>
            </div>
            <div class="flex items-center justify-between">
              <span class="text-sm font-medium text-green-600">
                {{ $t('SUBSCRIPTION_SETTINGS.UPGRADE.DISCOUNT') }}
                ({{ discountPercentage }}%)
              </span>
              <span class="text-sm font-medium text-green-600">
                {{
                  $t('SUBSCRIPTION_SETTINGS.UPGRADE.DISCOUNT_AMOUNT', {
                    amount: (originalAmount - totalAmount).toFixed(2),
                  })
                }}
              </span>
            </div>
          </div>
          <div class="flex items-center justify-between mb-2">
            <span class="text-sm font-medium text-n-slate-12">
              {{ $t('SUBSCRIPTION_SETTINGS.UPGRADE.TOTAL') }}
            </span>
            <span class="text-3xl font-bold text-n-brand">
              {{ `$${totalAmount}` }}
            </span>
          </div>
          <p class="text-xs text-n-slate-11">
            {{
              billingType === 'monthly'
                ? `${months} ${$t('SUBSCRIPTION_SETTINGS.UPGRADE.MONTHS')}`
                : $t('SUBSCRIPTION_SETTINGS.UPGRADE.YEARLY')
            }}
          </p>
        </div>

        <div class="mb-6">
          <div ref="paypalButtons" class="min-h-[50px]" />
        </div>

        <div
          v-if="processing"
          class="text-center py-4 text-sm font-medium text-n-brand"
        >
          <div class="flex items-center justify-center gap-2">
            <div
              class="w-4 h-4 border-2 border-n-brand border-t-transparent rounded-full animate-spin"
            />
            {{ $t('SUBSCRIPTION_SETTINGS.UPGRADE.PROCESSING') }}
          </div>
        </div>
      </div>
    </div>
  </Modal>
</template>
